/** Marketing copy for moneylensai.ca — keep in sync with Free / Pro gates. */

export const LANDING_PRO_PRICE_USD = 12.99;

export const LANDING_TITLE = 'MoneyLens AI';

export const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.fma.moneylens';
export const APP_STORE_ID = '6794596561';
export const APP_STORE_URL =
  'https://apps.apple.com/ca/app/moneylens-ai-budget-planner/id6794596561';

export const LANDING_DESCRIPTION =
  'See spending, savings, investments, debt, net worth, and retirement in one picture. Upload a statement or screenshot — no bank login. iPhone, Android, and the web.';

export const LANDING_HERO_LINE = 'One lens. All of your money.';

export const LANDING_HERO_BODY =
  'Budgeting apps stop at expenses. MoneyLens brings spending, savings, investments, debt, net worth, and retirement into one signed-in picture — from a statement or a screenshot. No bank login.';

export const LANDING_HERO_PILLARS = [
  'Spend',
  'Savings',
  'Investments',
  'Debt',
  'Net worth',
  'Retirement',
] as const;

export const LANDING_PRODUCT_TITLE = 'Every layer of your money';

export const LANDING_HOW_TITLE = 'A statement becomes the full picture.';

export const LANDING_APP_TITLE = 'iPhone, Android, and the web.';

export const LANDING_APP_LEAD =
  'Get the app on the App Store or Google Play, or keep going in the browser. Sign in once — household and imports follow the profile.';

/** Section ids become landing hashes: `/#product`, `/#how`, `/#app`, `/#pricing`. */
export const LANDING_NAV_ANCHORS = [
  { id: 'product', label: 'Product' },
  { id: 'how', label: 'How' },
  { id: 'app', label: 'App' },
  { id: 'pricing', label: 'Pricing' },
] as const;

/**
 * Free = see your money. Pro = MoneyLens works for you.
 * Matches product gates: 5 lifetime analyzes, household of 2, insight teasers,
 * no chat, no spend or card-due alerts (alerts are Pro in the API today).
 */
export const CAPABILITIES = [
  {
    id: 'import',
    title: 'Import',
    line: 'Statement or screenshot — spend or portfolio.',
    why: 'Card and bank photos become transactions. A brokerage screen becomes holdings. You never key a row.',
    shot: 'import',
    featured: true,
  },
  {
    id: 'cards',
    title: 'Cards',
    line: 'Spend by account. Due dates. A reminder before you miss one.',
    why: 'Each statement is its own row — spent, pay-in-full, and minimum. Pro can set a due-date alert (email or push).',
    shot: 'cards',
    featured: true,
  },
  {
    id: 'household',
    title: 'Household',
    line: 'Mine or Household — one tap.',
    why: 'Your cards alone, or both incomes together. Invite by email or text. Free is two people.',
    shot: 'household',
    featured: true,
  },
  {
    id: 'spend',
    title: 'Spend',
    line: 'This month’s mix — and how it sits next to the rest.',
    why: 'Housing, groceries, dining, unused subscriptions. One ring, then wealth, debt, and growth on the same account.',
    shot: 'spend',
  },
  {
    id: 'wealth',
    title: 'Wealth',
    line: 'What you own minus what you owe.',
    why: 'Net worth in your currency. 401(k), TFSA, RRSP — accounts that match the country you set.',
    shot: 'wealth',
  },
  {
    id: 'grow',
    title: 'Grow',
    line: 'What this balance becomes if you keep contributing.',
    why: 'Year-by-year compounding — early years feel slow, later ones accelerate.',
    shot: 'grow',
  },
  {
    id: 'loan',
    title: 'Loan',
    line: 'Minimum payment vs an extra $200.',
    why: 'See years cut and interest skipped before you change the autopay.',
    shot: 'loan',
  },
  {
    id: 'alerts',
    title: 'Alerts',
    line: 'You set the category, the cap, and the channel.',
    why: 'Dining over $400 this week? Email or push before the weekend.',
    shot: 'alerts',
    badge: 'Pro',
  },
  {
    id: 'ai',
    title: 'AI',
    line: 'A one-line read on the dashboard. Chat for the why.',
    why: 'Free sees the teaser. Pro can ask “what’s driving dining?” and get a plan.',
    shot: 'ai',
    badge: 'Pro',
  },
] as const;

export const HOW_STEPS = [
  {
    n: '01',
    title: 'Show us a statement',
    body: 'A PDF, a card screenshot, a bank app, or a brokerage screen. A photo is enough. We never ask for a bank password.',
  },
  {
    n: '02',
    title: 'AI assembles the picture',
    body: 'Spend becomes categories. A brokerage screen becomes holdings. Cards, debt, and net worth land on the same dashboard.',
  },
  {
    n: '03',
    title: 'See it on every device',
    body: 'Sign in with Google or your phone. The full picture follows the profile — iPhone, Android, and the web.',
  },
] as const;

/** Trust strip: mobile + web share one signed-in profile. Do not claim E2E encryption. */
export const PLATFORM_POINTS = [
  {
    id: 'devices',
    title: 'iPhone, Android, and the web',
    body: 'The app and moneylensai.ca are the same product. Sign in once. The dashboard is not locked to a browser or a phone.',
  },
  {
    id: 'sync',
    title: 'Profile and household stay together',
    body: 'Imports, members, and the numbers you already added follow the signed-in account. Upload on a laptop; check it on the phone.',
  },
  {
    id: 'privacy',
    title: 'No bank login',
    body: 'We never ask for institution passwords. You upload a statement or a screenshot. Data lives on your account so it can move with you safely.',
  },
] as const;

export const FREE_FEATURES = [
  'Spend, cards, wealth, grow, and loan',
  '5 lifetime screenshot imports (spend or portfolio)',
  'Household of 2',
  'One-line insight teasers',
  'iPhone, Android, and web — same account',
] as const;

export const FREE_LIMITS = [
  'No AI chat',
  'No spend or card-due alerts',
] as const;

export const PRO_FEATURES = [
  'Everything in Free',
  '200 AI analyses per billing period',
  'AI chat on every surface',
  'Spend and card-due alerts you set (email or push)',
  'Household up to 50',
] as const;

export const PLAN_COMPARE = [
  { label: 'Dashboard (spend, cards, wealth, grow, loan)', free: 'Yes', pro: 'Yes' },
  { label: 'Screenshot imports (spend or portfolio)', free: '5 lifetime', pro: '200 / period' },
  { label: 'iPhone, Android, and web', free: 'Yes', pro: 'Yes' },
  { label: 'Country accounts (401k, TFSA, RRSP)', free: 'Yes', pro: 'Yes' },
  { label: 'Insight teasers', free: 'Yes', pro: 'Yes' },
  { label: 'AI chat', free: '—', pro: 'Yes' },
  { label: 'Spend and card-due alerts', free: '—', pro: 'Yes' },
  { label: 'Household size', free: '2', pro: '50' },
] as const;
